import {
  ref,
  type Ref,
  watch,
  unref,
  computed,
  onMounted,
  onUnmounted,
} from 'vue'

export interface UseKeyboardNavigationOptions {
  containerRef: Ref<HTMLElement | null>
  itemSelector: string
  rovingTabIndex?: boolean
  trapFocus?: boolean
  typeahead?: boolean
  onSelected?: (element: HTMLElement) => void
  onClose?: () => void
  onSubmenuOpen?: (element: HTMLElement) => void
  onFocusLeave?: () => void
  isActive?: Ref<boolean> | boolean
}

export function useKeyboardNavigation({
  containerRef,
  itemSelector,
  rovingTabIndex = true,
  trapFocus = false,
  typeahead = false,
  onSelected,
  onClose,
  onSubmenuOpen,
  onFocusLeave,
  isActive: externalIsActive = true,
}: UseKeyboardNavigationOptions) {
  const items = ref<HTMLElement[]>([])
  const activeIndex = ref(-1)
  const searchString = ref('')
  let searchTimeout: ReturnType<typeof setTimeout> | null = null

  const isActive = computed(() => unref(externalIsActive))

  const getFocusableItems = (): HTMLElement[] =>
    containerRef.value
      ? (Array.from(
          containerRef.value.querySelectorAll(itemSelector)
        ) as HTMLElement[])
      : []

  const updateItems = () => {
    const focusableItems = getFocusableItems()
    items.value = focusableItems
    if (rovingTabIndex) {
      const currentFocusedElement = document.activeElement as HTMLElement
      const newlyFocusedIndex = items.value.findIndex(
        (item) => item === currentFocusedElement
      )

      if (focusableItems.length > 0 && activeIndex.value === -1) {
        activeIndex.value = newlyFocusedIndex > -1 ? newlyFocusedIndex : 0
      }

      focusableItems.forEach((item, index) => {
        item.setAttribute('tabindex', index === activeIndex.value ? '0' : '-1')
      })
    }
  }

  const setActiveIndex = (index: number) => {
    if (index < 0 || index >= items.value.length) {
      return
    }
    activeIndex.value = index
    if (rovingTabIndex) {
      items.value.forEach((item, i) => {
        item.setAttribute('tabindex', i === index ? '0' : '-1')
      })
    }
  }

  const setActiveItem = (index: number) => {
    setActiveIndex(index)
    if (items.value[index]) {
      items.value[index].focus()
    }
  }

  const activateAndFocusFirstItem = () => {
    if (items.value.length > 0) {
      setActiveItem(0)
    }
  }

  const handleFocusTrap = (event: KeyboardEvent) => {
    if (items.value.length === 0) {
      return
    }
    const firstElement = items.value[0]
    const lastElement = items.value[items.value.length - 1]

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault()
        lastElement.focus()
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault()
        firstElement.focus()
      }
    }
  }

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!isActive.value || items.value.length === 0) return

    if (trapFocus && event.key === 'Tab') {
      handleFocusTrap(event)
      return
    }

    if (activeIndex.value === -1) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault()
        setActiveItem(0)
      }
      return
    }

    if (searchTimeout) {
      clearTimeout(searchTimeout)
    }

    const keyActions: Record<string, () => void> = {
      ArrowDown: () =>
        setActiveItem((activeIndex.value + 1) % items.value.length),
      ArrowUp: () =>
        setActiveItem(
          (activeIndex.value - 1 + items.value.length) % items.value.length
        ),
      ArrowRight: () => onSubmenuOpen?.(items.value[activeIndex.value]),
      ArrowLeft: () => onClose?.(),
      Home: () => setActiveItem(0),
      End: () => setActiveItem(items.value.length - 1),
      Enter: () => onSelected?.(items.value[activeIndex.value]),
      ' ': () => onSelected?.(items.value[activeIndex.value]),
      Escape: () => onClose?.(),
    }

    const action = keyActions[event.key]

    if (action) {
      event.preventDefault()
      action()
    } else if (
      typeahead &&
      event.key.length === 1 &&
      !event.metaKey &&
      !event.ctrlKey
    ) {
      event.preventDefault()
      handleTypeahead(event.key)
    }
  }

  const handleTypeahead = (key: string) => {
    searchString.value += key.toLowerCase()
    searchTimeout = setTimeout(() => {
      searchString.value = ''
    }, 500)

    const matchingIndex = items.value.findIndex((item) =>
      item.textContent?.trim().toLowerCase().startsWith(searchString.value)
    )

    if (matchingIndex !== -1) {
      setActiveItem(matchingIndex)
    }
  }

  const handleFocusIn = (event: FocusEvent) => {
    const target = event.target as HTMLElement
    const index = items.value.indexOf(target)
    if (index !== -1) {
      setActiveIndex(index)
    }
  }

  const handleFocusOut = (event: FocusEvent) => {
    if (
      isActive.value &&
      containerRef.value &&
      !containerRef.value.contains(event.relatedTarget as Node)
    ) {
      onFocusLeave?.()
    }
  }

  watch(
    () => containerRef.value,
    (newContainer, oldContainer) => {
      if (oldContainer) {
        oldContainer.removeEventListener('keydown', handleKeyDown)
        oldContainer.removeEventListener('focusin', handleFocusIn)
        oldContainer.removeEventListener('focusout', handleFocusOut)
      }
      if (newContainer) {
        newContainer.addEventListener('keydown', handleKeyDown)
        newContainer.addEventListener('focusin', handleFocusIn)
        newContainer.addEventListener('focusout', handleFocusOut)
        updateItems()
      }
    },
    { immediate: true }
  )

  onMounted(() => {
    updateItems()
    const observer = new MutationObserver(updateItems)
    if (containerRef.value) {
      observer.observe(containerRef.value, { childList: true, subtree: true })
    }
    onUnmounted(() => {
      observer.disconnect()
    })
  })

  onUnmounted(() => {
    if (containerRef.value) {
      containerRef.value.removeEventListener('keydown', handleKeyDown)
      containerRef.value.removeEventListener('focusin', handleFocusIn)
      containerRef.value.removeEventListener('focusout', handleFocusOut)
    }
  })

  return {
    items,
    activeIndex,
    setActiveItem,
    updateItems,
    activateAndFocusFirstItem,
  }
}
